// src/components/AthleteProfile/AthleteProfile.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../components/styles/AthleteProfile.css';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const AthleteProfile = ({ isTrainerView = false, athleteId = null }) => {
  const { t } = useTranslation();
  const userId = isTrainerView ? athleteId : localStorage.getItem('userId');

  const [profileData, setProfileData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    username: '',
    birthDate: '',
    gender: '',
    weight: 0,
    height: 0,
    country: '',
    city: '',
    club: '',
    coach: '',
    disciplines: [],
    profilePicture: '',
    trainingZones: [],
  });

  // eslint-disable-next-line
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const disciplines = ['Trail', 'Running', 'Ciclismo', 'Natación', 'Triatlón'];
  const disciplineLabels = {
    Trail: t('athleteProfile.disciplinesLabels.trail'),
    Running: t('athleteProfile.disciplinesLabels.running'),
    Ciclismo: t('athleteProfile.disciplinesLabels.cycling'),
    Natación: t('athleteProfile.disciplinesLabels.swimming'),
    Triatlón: t('athleteProfile.disciplinesLabels.triathlon'),
  };
  const [editMode, setEditMode] = useState(false);
  const [birthDate, setBirthDate] = useState('');

  const formatTime = useCallback((value) => {
    const seconds = parseInt(value, 10);
    if (isNaN(seconds)) return t('athleteProfile.notAvailable');
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, [t]);

  const calculateAge = useCallback(() => {
    if (!birthDate) return '';
    const birthDateObj = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birthDateObj.getFullYear();
    if (
      today.getMonth() < birthDateObj.getMonth() ||
      (today.getMonth() === birthDateObj.getMonth() && today.getDate() < birthDateObj.getDate())
    ) {
      age--;
    }
    return age;
  }, [birthDate]);

  const handleSaveProfile = () => {
    setTimeout(async () => {
      console.log('🧠 Fecha en profileData al guardar (con delay):', profileData.birthDate);
      console.log('📤 Estado birthDate (input):', birthDate);
      console.log('📤 Estado profileData.birthDate (debug):', profileData.birthDate);

      try {
        const currentUserId = isTrainerView ? athleteId : localStorage.getItem('userId');

        if (!currentUserId) {
          setMessage(t('athleteProfile.messages.userIdNotFound'));
          return;
        }

        if (!birthDate || birthDate.trim() === '') {
          setMessage(t('athleteProfile.messages.invalidBirthDate'));
          return;
        }

        if (!profileData.gender || profileData.gender.trim() === '') {
          setMessage(t('athleteProfile.messages.genderRequired'));
          return;
        }

        const dataToSend = {
          userId: currentUserId,
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          email: profileData.email || '',
          birthDate: birthDate || '',
          city: profileData.city || '',
          club: profileData.club || '',
          coach: profileData.coach || '',
          country: profileData.country || '',
          disciplines: profileData.disciplines || [],
          gender: profileData.gender || 'Otro',
          height: profileData.height || 0,
          weight: profileData.weight || 0,
          profilePicture: profileData.profilePicture || '',
        };

        console.log('📦 Fecha a enviar:', `"${dataToSend.birthDate}"`, 'Largo:', dataToSend.birthDate.length);

        const res = await axios.post(`${API_BASE_URL}/api/athletes/save-profile`, dataToSend);
        console.log('✅ Respuesta del backend:', res.data);

        setProfileData((prev) => ({
          ...prev,
          ...dataToSend,
          birthDate: dataToSend.birthDate,
        }));

        setMessage(t('athleteProfile.messages.profileSaved'));
        setEditMode(false);
      } catch (error) {
        console.error('❌ Error al guardar el perfil:', error.response?.data || error.message);
        setMessage(t('athleteProfile.messages.saveError'));
      }
    }, 100);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!userId) {
          setMessage(t('athleteProfile.messages.userIdNotFound'));
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/athletes/profile/${userId}`);
        const athleteData = response.data.athlete;

        let formattedBirthDate = '';
        if (athleteData.birthDate) {
          const parsedDate = new Date(athleteData.birthDate);
          if (!isNaN(parsedDate)) {
            formattedBirthDate = parsedDate.toISOString().split('T')[0];
          }
        }

        setBirthDate(formattedBirthDate || '');

        const profilePhotoUrl = athleteData.profilePhoto?.trim()
          ? (athleteData.profilePhoto.startsWith('data:') || athleteData.profilePhoto.includes('http')
              ? athleteData.profilePhoto
              : `${API_BASE_URL}${athleteData.profilePhoto}?t=${Date.now()}`)
          : `${API_BASE_URL}/uploads/athletes/default.jpg`;

        setProfileData({
          id: athleteData._id || '',
          firstName: athleteData.firstName || '',
          lastName: athleteData.lastName || '',
          name: athleteData.name || t('athleteProfile.notAvailable'),
          email: athleteData.email?.trim() !== '' ? athleteData.email : t('athleteProfile.notRegistered'),
          username: athleteData.username || '',
          gender: athleteData.gender || 'Otro',
          weight: athleteData.weight || 0,
          height: athleteData.height || 0,
          country: athleteData.country || '',
          city: athleteData.city || '',
          club: athleteData.club || '',
          coach: athleteData.coach || '',
          disciplines: Array.isArray(athleteData.disciplines) ? athleteData.disciplines : [],
          profilePicture: profilePhotoUrl,
          trainingZones: typeof athleteData.trainingZones === 'object' ? athleteData.trainingZones : {},
          birthDate: formattedBirthDate || '',
        });

        setLoading(false);
      } catch (error) {
        console.error('❌ Error al cargar el perfil:', error.response?.data || error.message);
        setMessage(t('athleteProfile.messages.loadError'));
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, t]);

  if (loading) return <p>{t('athleteProfile.loading')}</p>;

  return (
    <div className="athlete-profile-container">
      <header className="profile-header">
        {isTrainerView && (
          <div
            className="trainer-banner"
            style={{ padding: '10px 20px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}
          >
            <h5 style={{ margin: 0, fontSize: '1.1rem' }}>
              {t('athleteProfile.viewingProfileOf')}: <strong>{`${profileData.firstName} ${profileData.lastName}`}</strong>
            </h5>
          </div>
        )}
      </header>

      <div className="profile-grid updated-layout">
        <section className="profile-card">
          <h2>{t('athleteProfile.personalInfo')}</h2>
          <img
            src={
              profileData.profilePicture?.startsWith('data:') || profileData.profilePicture?.includes('http')
                ? profileData.profilePicture
                : `${API_BASE_URL}${profileData.profilePicture}?t=${Date.now()}`
            }
            className="profile-img"
            alt={t('athleteProfile.profileAlt')}
          />
          {editMode && (
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
                if (!allowedTypes.includes(file.type)) {
                  setMessage(t('athleteProfile.messages.onlyJpgPng'));
                  return;
                }

                if (file.size > 2 * 1024 * 1024) {
                  setMessage(t('athleteProfile.messages.imageTooLarge'));
                  return;
                }

                const formData = new FormData();
                formData.append('profilePhoto', file);

                try {
                  const currentUserId = (isTrainerView && athleteId) ? athleteId : (window.__labSelectedAthleteId || localStorage.getItem('userId'));
                  const response = await axios.post(
                    `${API_BASE_URL}/api/athletes/upload-photo/${currentUserId}`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );

                  setProfileData((prev) => ({
                    ...prev,
                    profilePicture: `${response.data.filePath}`,
                  }));
                  setMessage(t('athleteProfile.messages.imageUpdated'));
                } catch (error) {
                  console.error('❌ Error al subir la imagen:', error);
                  setMessage(t('athleteProfile.messages.imageUploadError'));
                }
              }}
            />
          )}

          <p>
            <strong>{t('athleteProfile.name')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.firstName}
                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                placeholder={t('athleteProfile.name')}
              />
            ) : `${profileData.firstName} ${profileData.lastName}`}
          </p>

          <p>
            <strong>{t('athleteProfile.lastName')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                placeholder={t('athleteProfile.lastName')}
              />
            ) : null}
          </p>

          <p>
            <strong>{t('athleteProfile.email')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              />
            ) : profileData.email}
          </p>

          <p>
            <strong>{t('athleteProfile.username')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.username}
                onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
              />
            ) : profileData.username}
          </p>
        </section>

        <section className="profile-card">
          <h2>{t('athleteProfile.physicalInfo')}</h2>
          <p>
            <strong>{t('athleteProfile.gender')}:</strong>{' '}
            {editMode ? (
              <select
                value={profileData.gender}
                onChange={(e) => setProfileData({ ...profileData, gender: e.target.value })}
              >
                <option value="Masculino">{t('athleteProfile.genderOptions.male')}</option>
                <option value="Femenino">{t('athleteProfile.genderOptions.female')}</option>
                <option value="Otro">{t('athleteProfile.genderOptions.other')}</option>
              </select>
            ) : profileData.gender}
          </p>

          <p>
            <strong>{t('athleteProfile.birthDate')}:</strong>{' '}
            {editMode ? (
              <input
                type="date"
                name="birthDate"
                value={birthDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setBirthDate(selectedDate);
                  setProfileData((prev) => ({
                    ...prev,
                    birthDate: selectedDate,
                  }));
                }}
              />
            ) : (profileData.birthDate || t('athleteProfile.notAvailable'))}
          </p>

          <p><strong>{t('athleteProfile.age')}:</strong> {calculateAge()}</p>

          <p>
            <strong>{t('athleteProfile.weight')}:</strong>{' '}
            {editMode ? (
              <input
                type="number"
                value={profileData.weight}
                onChange={(e) => setProfileData({ ...profileData, weight: e.target.value })}
              />
            ) : profileData.weight}
          </p>

          <p>
            <strong>{t('athleteProfile.height')}:</strong>{' '}
            {editMode ? (
              <input
                type="number"
                value={profileData.height}
                onChange={(e) => setProfileData({ ...profileData, height: e.target.value })}
              />
            ) : profileData.height}
          </p>
        </section>

        <section className="profile-card">
          <h2>{t('athleteProfile.locationClub')}</h2>
          <p>
            <strong>{t('athleteProfile.country')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.country}
                onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
              />
            ) : (profileData.country || t('athleteProfile.notSpecified'))}
          </p>

          <p>
            <strong>{t('athleteProfile.city')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.city}
                onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
              />
            ) : (profileData.city || t('athleteProfile.notSpecified'))}
          </p>

          <p>
            <strong>{t('athleteProfile.club')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.club}
                onChange={(e) => setProfileData({ ...profileData, club: e.target.value })}
              />
            ) : (profileData.club || t('athleteProfile.notSpecified'))}
          </p>

          <p>
            <strong>{t('athleteProfile.coach')}:</strong>{' '}
            {editMode ? (
              <input
                value={profileData.coach}
                onChange={(e) => setProfileData({ ...profileData, coach: e.target.value })}
              />
            ) : (profileData.coach || t('athleteProfile.notSpecified'))}
          </p>
        </section>

        <section className="profile-card">
          <h2>{t('athleteProfile.disciplines')}</h2>
          {disciplines.map((d) => (
            <button
              key={disciplineLabels[d] || d}
              className={profileData.disciplines.includes(d) ? 'selected' : ''}
              disabled={!editMode}
              onClick={() => {
                if (!editMode) return;
                setProfileData((prev) => {
                  const updated = prev.disciplines.includes(d)
                    ? prev.disciplines.filter((disc) => disc !== d)
                    : [...prev.disciplines, d];
                  return { ...prev, disciplines: updated };
                });
              }}
            >
              {disciplineLabels[d] || d}
            </button>
          ))}
        </section>
      </div>

      <h2 className="zones-title">{t('athleteProfile.trainingZones')}</h2>
      <div className="zones-container">
        {profileData.trainingZones?.FC && (
          <section className="zone-block">
            <h3>{t('athleteProfile.heartRate')}</h3>
            <h4 className="threshold-info">
              {t('athleteProfile.thresholdHeartRate')}: {profileData.trainingZones.FC.threshold} bpm
            </h4>
            <table>
              <thead>
                <tr>
                  <th>{t('athleteProfile.zone')}</th>
                  <th>{t('athleteProfile.min')}</th>
                  <th>{t('athleteProfile.max')}</th>
                </tr>
              </thead>
              <tbody>
                {profileData.trainingZones.FC.zones.map((z, i) => (
                  <tr key={i}>
                    <td>{z.name}</td>
                    <td>{z.min} bpm</td>
                    <td>{z.max} bpm</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {profileData.trainingZones?.VP && (
          <section className="zone-block">
            <h3>{t('athleteProfile.pace')}</h3>
            <h4 className="threshold-info">
              {t('athleteProfile.thresholdSpeed')}: {formatTime(profileData.trainingZones.VP.threshold)}
            </h4>
            <table>
              <thead>
                <tr>
                  <th>{t('athleteProfile.zone')}</th>
                  <th>{t('athleteProfile.min')}</th>
                  <th>{t('athleteProfile.max')}</th>
                </tr>
              </thead>
              <tbody>
                {profileData.trainingZones.VP.zones.map((z, i) => (
                  <tr key={i}>
                    <td>{z.name}</td>
                    <td>{formatTime(z.min)}</td>
                    <td>{formatTime(z.max)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {profileData.trainingZones?.CP && (
          <section className="zone-block">
            <h3>{t('athleteProfile.power')}</h3>
            <h4 className="threshold-info">
              {t('athleteProfile.thresholdPower')}: {profileData.trainingZones.CP.threshold} watts
            </h4>
            <table>
              <thead>
                <tr>
                  <th>{t('athleteProfile.zone')}</th>
                  <th>{t('athleteProfile.min')}</th>
                  <th>{t('athleteProfile.max')}</th>
                </tr>
              </thead>
              <tbody>
                {profileData.trainingZones.CP.zones.map((z, i) => (
                  <tr key={i}>
                    <td>{z.name}</td>
                    <td>{z.min} w</td>
                    <td>{z.max} w</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {profileData.trainingZones?.SWIM && (
          <section className="zone-block">
            <h3>{t('athleteProfile.swimmingCss')}</h3>
            <h4 className="threshold-info">
              {t('athleteProfile.threshold')}: {formatTime(profileData.trainingZones.SWIM.threshold)} min/100m
            </h4>
            <table>
              <thead>
                <tr>
                  <th>{t('athleteProfile.zone')}</th>
                  <th>{t('athleteProfile.min')}</th>
                  <th>{t('athleteProfile.max')}</th>
                </tr>
              </thead>
              <tbody>
                {profileData.trainingZones.SWIM.zones.map((z, i) => (
                  <tr key={i}>
                    <td>{z.name}</td>
                    <td>{formatTime(z.min)}</td>
                    <td>{formatTime(z.max)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      {message && (
        <div className="alert alert-info text-center mt-3">
          {message}
        </div>
      )}

      <div className="profile-actions centered-buttons">
        {editMode ? (
          <button onClick={handleSaveProfile}>{t('athleteProfile.saveChanges')}</button>
        ) : (
          <button onClick={() => setEditMode(true)}>{t('athleteProfile.editProfile')}</button>
        )}

        {!isTrainerView && (
          <button className="btn btn-primary" onClick={() => navigate('/athlete-dashboard')}>
            {t('athleteProfile.backToDashboard')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AthleteProfile;